/**
 * Window page
 * Wrapper for some basic logic
 */

/**
 * Third-party imports
 */
import React, { PureComponent } from 'react';
import { Helmet } from 'react-helmet';
import { FiMenu } from 'react-icons/fi';
import { withLocalize } from 'react-localize-redux';
import Notifications, {
  error as errorNotification,
  success
} from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { Match } from '@reach/router';
import { bindActionCreators, compose } from 'redux';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';

import Menu from '../../components/menu';
import NavBar from '../../components/nav-bar';
import SEO from '../../components/seo';
import {
  COUPON_REFERAL_CODE,
  DEFAULT_COLOURS,
  GAME_START_CODE,
  GAME_TO_COLOURS
} from '../../constants';
import firebase from '../../firebase';
/**
 * Base translations
 * TODO: move component specific translations from this file
 */
import jaLocale from '../../locale/ja.locale';
/**
 * Action creators
 */
import { startAuthenticating, stopAuthenticating } from '../../modules/auth';
import { asyncResourceLoaded } from '../../modules/global/action-creators';
import { openMenu, setMenuState } from '../../modules/menu';
import GameController from '../game-controller';

const GlobalStyle = createGlobalStyle`
*::-webkit-scrollbar-thumb {
  background-color: ${props => props.theme.primary};
}
`;

/**
 * Main Window container
 * Uncapsulates everything
 */
const MainWindow = styled.main``;

const MenuIcon = styled.div`
  height: 3.5rem;
  width: 5rem;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  z-index: 4;
  left: 0em;
  top: 0em;
  cursor: pointer;
`;

const queryParamsToStore = [COUPON_REFERAL_CODE, GAME_START_CODE];

/**
 * Root component used to house the app
 */
class Window extends PureComponent {
  constructor(props) {
    super(props);
    this.props.addTranslationForLanguage(jaLocale, 'ja');
  }
  /**
   * Initialization code ran when the app boots up
   */
  componentDidMount() {
    // Async load heavy required resources if they are not loaded on page directly
    const promises = [];
    if (firebase.database === undefined) {
      promises.push(
        import('firebase/database').then(() => {
          this.props.asyncResourceLoaded('database');
        })
      );
    } else {
      this.props.asyncResourceLoaded('database');
    }
    if (firebase.auth === undefined) {
      promises.push(
        import('firebase/auth').then(() => {
          // Listen to the Firebase Auth state
          this.props.startAuthenticating();
        })
      );
    } else {
      this.props.startAuthenticating();
    }
    Promise.all(promises).catch(err => {
      console.log(err);
      this.props.errorNotification({
        title: this.props.translate(
          `global.authResourceFailedNotification.title`
        ),
        message: this.props.translate(
          `global.authResourceFailedNotification.message`
        ),
        autoDismiss: 0,
        dismissible: 'none',
        action: {
          label: this.props.translate(
            `global.authResourceFailedNotification.refresh`
          ),
          callback: () => window.location.reload()
        }
      });
    });

    const queryParams = new URLSearchParams(window.location.search);

    /**
     * URL Params
     */
    // Save important query params
    queryParamsToStore.forEach(param => {
      const value = queryParams.get(param);
      if (value) {
        window.localStorage.setItem(param, value);
      }
    });
  }
  /**
   * Clean up after the app unmounts
   */
  componentWillUnmount() {
    // Make sure we un-register Firebase observers when the component unmounts.
    stopAuthenticating();
  }

  /**
   * MenuIcon Click Callback
   * Open the menu, and send the message to GTM
   */
  clickMenu = () => {
    this.props.openMenu();
    window.dataLayer.push({
      event: 'MENU_CLICKED'
    });
  };

  /**
   * @inheritDoc
   */
  render() {
    return (
      <Match path="/games/:gameId/*">
        {({ match }) => {
          const theme =
            (match && GAME_TO_COLOURS[match.gameId]) || DEFAULT_COLOURS;

          return (
            <>
              {match && <GameController gameId={match.gameId} />}
              <ThemeProvider theme={theme}>
                <SEO gameId={match ? match.gameId : null} />
                <Helmet>
                  <meta charset="utf-8" />
                  <meta
                    content="width=device-width, initial-scale=1, shrink-to-fit=no"
                    name="viewport"
                  />
                  <meta content={theme.primary} name="theme-color" />
                </Helmet>
                <GlobalStyle />
                <MainWindow>
                  <Notifications notifications={this.props.notifications} />
                  <Menu
                    authenticated={this.props.authenticated}
                    authLoaded={this.props.authLoaded}
                    onSetRequest={this.props.setMenuState}
                    open={this.props.menuOpen}
                  >
                    {this.props.isContinue == false  && (
                    <MenuIcon onClick={this.clickMenu}>
                      <FiMenu />
                    </MenuIcon>
                    )}
                    <NavBar gameId={match && match.gameId} />
                    {this.props.children}
                  </Menu>
                </MainWindow>
              </ThemeProvider>
            </>
          );
        }}
      </Match>
    );
  }
}

/**
 * Map Redux State to props
 */
const mapStateToProps = ({ notifications, menu, auth, global }) => ({
  authenticated: auth.authenticated,
  authLoaded: auth.authLoaded,
  databaseLoaded: global.loadedResources.database,
  notifications,
  menuOpen: menu.open,
  isContinue: global.isContinue
});

/**
 * Map Redux Dispatch to props
 */
const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      startAuthenticating,
      success,
      openMenu,
      setMenuState,
      asyncResourceLoaded,
      errorNotification
    },
    dispatch
  )
});

export default compose(
  withLocalize,
  connect(mapStateToProps, mapDispatchToProps)
)(Window);
